import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { LogOut, Trash2, Mail, MapPin, Package, Loader2, Inbox, Search, RefreshCw, KeyRound } from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { adminApi, getToken, clearToken } from "@/lib/adminApi";
import { navigate } from "@/lib/nav";

const STATUS_STYLES = {
  New: "bg-[#E65100] text-white",
  Contacted: "bg-[#D4AF37] text-[#0B1320]",
  Closed: "bg-slate-600 text-slate-200",
};
const STATUSES = ["New", "Contacted", "Closed"];

export default function AdminDashboard() {
  const [quotes, setQuotes] = useState([]);
  const [stats, setStats] = useState({ total: 0, New: 0, Contacted: 0, Closed: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [confirmId, setConfirmId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [q, s] = await Promise.all([adminApi.quotes(), adminApi.stats()]);
      setQuotes(q.data);
      setStats(s.data);
    } catch (err) {
      if (err.response?.status === 401) {
        clearToken();
        navigate("/admin/login");
      } else {
        toast.error("Failed to load quotes");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin/login");
      return;
    }
    load();
  }, [load]);

  const changeStatus = async (id, status) => {
    try {
      const { data } = await adminApi.updateStatus(id, status);
      setQuotes((qs) => qs.map((q) => (q.id === id ? data : q)));
      const s = await adminApi.stats();
      setStats(s.data);
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Update failed");
    }
  };

  const remove = async (id) => {
    try {
      await adminApi.remove(id);
      setQuotes((qs) => qs.filter((q) => q.id !== id));
      setConfirmId(null);
      const s = await adminApi.stats();
      setStats(s.data);
      toast.success("Quote deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const logout = () => {
    clearToken();
    toast.success("Signed out");
    navigate("/admin/login");
  };

  const visible = quotes.filter((q) => {
    const okStatus = filter === "All" || q.status === filter;
    const t = search.toLowerCase();
    const okSearch = !t || [q.name, q.email, q.company, q.destination, q.cargo_type].some((v) => (v || "").toLowerCase().includes(t));
    return okStatus && okSearch;
  });

  return (
    <div className="min-h-screen bg-[#0B1320] text-white">
      {/* Header */}
      <header className="glass sticky top-0 z-40 border-b border-white/10">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
          <div className="flex items-center gap-3">
            <Logo className="h-10 w-10" />
            <div>
              <p className="font-display text-sm font-extrabold uppercase text-white">Quote Inbox</p>
              <p className="data-label text-[0.55rem]">Suryavansh Exim Admin</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => {
              const pass = prompt("Enter new admin password:");
              if (pass && pass.trim().length >= 4) {
                localStorage.setItem("sv_admin_password", pass.trim());
                toast.success("Admin password updated successfully!");
              } else if (pass) {
                toast.error("Password must be at least 4 characters long");
              }
            }} className="flex items-center gap-2 rounded-full border border-white/15 px-3 py-2 text-sm text-slate-300 transition-colors hover:border-[#E65100] hover:text-[#FF6D00]">
              <KeyRound className="h-4 w-4" /> <span className="hidden sm:inline">Change Password</span>
            </button>
            <button onClick={load} data-testid="admin-refresh-btn" className="flex items-center gap-2 rounded-full border border-white/15 px-3 py-2 text-sm text-slate-300 transition-colors hover:border-[#D4AF37] hover:text-[#D4AF37]">
              <RefreshCw className="h-4 w-4" /> <span className="hidden sm:inline">Refresh</span>
            </button>
            <button onClick={logout} data-testid="admin-logout-btn" className="flex items-center gap-2 rounded-full bg-[#E65100] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#FF6D00]">
              <LogOut className="h-4 w-4" /> <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 py-8 sm:px-8">
        {/* Stats */}
        <div className="mb-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {[
            { k: "total", label: "Total Requests", color: "text-white" },
            { k: "New", label: "New", color: "text-[#E65100]" },
            { k: "Contacted", label: "Contacted", color: "text-[#D4AF37]" },
            { k: "Closed", label: "Closed", color: "text-slate-400" },
          ].map((c) => (
            <div key={c.k} data-testid={`admin-stat-${c.k}`} className="rounded-2xl border border-white/10 bg-[#111C2E] p-5">
              <p className={`font-display text-4xl font-extrabold ${c.color}`}>{stats[c.k] ?? 0}</p>
              <p className="mt-1 data-label">{c.label}</p>
            </div>
          ))}
        </div>

        {/* Controls */}
        <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-2">
            {["All", ...STATUSES].map((f) => (
              <button
                key={f}
                data-testid={`admin-filter-${f}`}
                onClick={() => setFilter(f)}
                className={`rounded-full px-4 py-2 text-sm font-semibold transition-all ${filter === f ? "bg-[#E65100] text-white" : "border border-white/15 text-slate-300 hover:border-[#E65100]"}`}
              >
                {f}
              </button>
            ))}
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <input
              data-testid="admin-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, email, cargo..."
              className="w-full rounded-full border border-white/10 bg-[#111C2E] py-2.5 pl-10 pr-4 text-sm text-white outline-none focus:border-[#E65100] sm:w-72"
            />
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center py-24 text-slate-400">
            <Loader2 className="mr-2 h-6 w-6 animate-spin" /> Loading quotes...
          </div>
        ) : visible.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-white/10 py-24 text-slate-500" data-testid="admin-empty">
            <Inbox className="mb-3 h-10 w-10" />
            No quote requests found.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2" data-testid="admin-quote-list">
            <AnimatePresence mode="popLayout">
              {visible.map((q) => (
                <motion.article
                  key={q.id}
                  layout
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ duration: 0.35 }}
                  data-testid={`admin-quote-${q.id}`}
                  className="rounded-2xl border border-white/10 bg-[#111C2E] p-5"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-display text-lg font-bold text-white">{q.name}</h3>
                        <span className={`rounded-full px-2.5 py-0.5 text-[0.6rem] font-bold uppercase ${q.status === "Export" ? "" : ""} ${q.trade === "Import" ? "bg-[#D4AF37]/20 text-[#D4AF37]" : "bg-[#E65100]/20 text-[#FF6D00]"}`}>{q.trade}</span>
                      </div>
                      {q.company ? <p className="text-xs text-slate-500">{q.company}</p> : null}
                    </div>
                    <span className={`rounded-full px-3 py-1 text-[0.65rem] font-bold uppercase ${STATUS_STYLES[q.status] || STATUS_STYLES.New}`}>{q.status}</span>
                  </div>

                  <div className="mt-4 space-y-2 text-sm text-slate-300">
                    <a href={`mailto:${q.email}`} className="flex items-center gap-2 hover:text-[#D4AF37]"><Mail className="h-4 w-4 text-[#E65100]" /> {q.email}</a>
                    {(q.origin || q.destination) && (
                      <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-[#E65100]" /> {q.origin || "—"} → {q.destination || "—"} <span className="text-slate-500">· {q.mode}</span></p>
                    )}
                    {q.cargo_type ? <p className="flex items-center gap-2"><Package className="h-4 w-4 text-[#E65100]" /> {q.cargo_type}</p> : null}
                    {q.message ? <p className="rounded-lg bg-black/20 p-2 text-xs text-slate-400">{q.message}</p> : null}
                  </div>

                  <div className="mt-4 flex items-center justify-between border-t border-white/10 pt-4">
                    <div className="flex gap-1.5">
                      {STATUSES.map((s) => (
                        <button
                          key={s}
                          data-testid={`admin-setstatus-${q.id}-${s}`}
                          onClick={() => changeStatus(q.id, s)}
                          className={`rounded-md px-2.5 py-1 text-xs font-medium transition-all ${q.status === s ? STATUS_STYLES[s] : "bg-white/5 text-slate-400 hover:bg-white/10"}`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                    <button
                      data-testid={`admin-delete-${q.id}`}
                      onClick={() => setConfirmId(q.id)}
                      className="flex items-center gap-1 rounded-md px-2 py-1 text-xs text-slate-400 transition-colors hover:bg-red-500/10 hover:text-red-400"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <p className="mt-3 font-mono-t text-[0.6rem] text-slate-600">{new Date(q.created_at).toLocaleString()}</p>
                </motion.article>
              ))}
            </AnimatePresence>
          </div>
        )}
      </main>

      {/* Delete confirm */}
      <AnimatePresence>
        {confirmId && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setConfirmId(null)} />
            <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }} className="glass relative z-10 w-full max-w-sm rounded-2xl p-6 text-center">
              <Trash2 className="mx-auto h-10 w-10 text-red-400" />
              <h3 className="mt-4 font-display text-xl font-bold text-white">Delete this quote?</h3>
              <p className="mt-1 text-sm text-slate-400">This action cannot be undone.</p>
              <div className="mt-6 flex gap-3">
                <button onClick={() => setConfirmId(null)} className="flex-1 rounded-full border border-white/15 py-2.5 text-sm text-slate-300 hover:bg-white/5">Cancel</button>
                <button data-testid="admin-delete-confirm" onClick={() => remove(confirmId)} className="flex-1 rounded-full bg-red-500 py-2.5 text-sm font-semibold text-white hover:bg-red-600">Delete</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
